/* eslint-disable jsdoc/require-jsdoc -- 纯映射和边界守卫由契约测试覆盖。 */
import type { components } from "../../../api/generated";
import type {
  StayGeoVerificationStatus,
  StayResourceGeoCandidate,
  StayResourceGeoReviewMutation,
  StayResourceGeoReviewAuditItem,
  StayResourceGeoReviewResult,
  StayResourceGeoVerificationResult,
  StayResourceHousLevelOption,
  StayResourceListPage,
  StayResourceListParams,
  StayResourceReviewMutation,
  StayResourceReviewStatus,
  StayResourceLegacyMappingItem,
  StayResourceMediaItem,
  StayResourceMediaMutation,
  StayResourceMediaOrderItem,
  StayResourceSkuItem,
  StayResourceSkuMutation,
  StayResourceSpuItem,
  StayResourceSpuMutation,
  StayResourceSupplierItem,
  StayResourceSupplierMutation,
  StayResourceSupplierOption,
} from "../types";

type GeneratedStaySpuRaw = components["schemas"]["StayResourceSpuItem"];
type GeneratedStayListPageRaw = components["schemas"]["StayResourceListPage"];
type GeneratedStaySkuRaw = components["schemas"]["StayResourceSkuItem"];
type GeneratedStayMediaRaw = components["schemas"]["StayResourceMediaItem"];
type GeneratedStaySupplierRaw = components["schemas"]["StayResourceSupplierItem"];
type GeneratedStayGeoCandidateRaw = components["schemas"]["StayResourceGeoCandidateRow"];
type GeneratedStayReviewMutation = components["schemas"]["StayResourceReviewMutation"];
type GeneratedDimHousLevelRaw = components["schemas"]["DimHousLevelOption"];

export type StayResourceSpuRaw = GeneratedStaySpuRaw;
export type StayResourceListPageRaw = GeneratedStayListPageRaw;
export type StayResourceSkuRaw = GeneratedStaySkuRaw;
export type StayResourceMediaRaw = GeneratedStayMediaRaw;
export type StayResourceSupplierRaw = GeneratedStaySupplierRaw;
export type StayResourceGeoCandidateRaw = Omit<
  GeneratedStayGeoCandidateRaw,
  "lng" | "lat" | "match_score"
> & {
  lng: string | number;
  lat: string | number;
  match_score: string | number | null;
};
export type StayResourceGeoVerificationResultRaw =
  components["schemas"]["StayResourceGeoVerificationResult"];
export type StayResourceGeoReviewResultRaw =
  components["schemas"]["StayResourceGeoReviewResult"];
export type StayResourceGeoReviewAuditRaw =
  components["schemas"]["StayResourceGeoReviewAuditRow"];
export type StayResourceSupplierOptionRaw =
  components["schemas"]["StayResourceSupplierOption"];
export type StayResourceLegacyMappingRaw =
  components["schemas"]["StayResourceLegacyMappingItem"];
export type StayResourceHousLevelRaw = GeneratedDimHousLevelRaw;

export function mapStayResourceListParams(params: StayResourceListParams) {
  return {
    page: params.page,
    page_size: params.pageSize,
    keyword: optionalText(params.keyword),
    country: optionalText(params.country),
    province: optionalText(params.province),
    city: optionalText(params.city),
    district: optionalText(params.district),
    stay_level: optionalText(params.stayLevel),
    hotel_type: optionalText(params.hotelType),
    status: optionalText(params.status),
    is_disabled: params.isDisabled,
    geo_status: params.geoStatus,
    review_status: params.reviewStatus,
    sort_field: params.sortField,
    sort_direction: params.sortDirection,
  };
}

export function mapStayResourceReview(
  payload: StayResourceReviewMutation,
): GeneratedStayReviewMutation {
  return {
    decision: payload.decision,
    reason: optionalText(payload.reason) ?? null,
  };
}

export function mapStayResourceSpuMutation(payload: StayResourceSpuMutation) {
  return {
    public_phones: payload.publicPhones ?? null,
    room_count: payload.roomCount ?? null,
    opening_year: payload.openingYear ?? null,
    renovation_year: payload.renovationYear ?? null,
    platform_rating: payload.platformRating ?? null,
    hotel_name: payload.hotelName.trim(),
    hotel_short_name: optionalText(payload.hotelShortName),
    country: payload.country.trim(),
    province: payload.province.trim(),
    city: payload.city.trim(),
    district: optionalText(payload.district),
    address: optionalText(payload.address),
    stay_level: optionalText(payload.stayLevel),
    hotel_type: optionalText(payload.hotelType),
    remark: payload.remark.trim(),
  };
}

export function mapStayResourceSkuMutation(payload: StayResourceSkuMutation) {
  return {
    sku_name: payload.skuName.trim(),
    room_type: payload.roomType.trim(),
    room_usage_label: optionalText(payload.roomUsageLabel),
    room_capacity: payload.roomCapacity,
    remark: payload.remark.trim(),
  };
}

export function mapStayResourceMediaMutation(payload: StayResourceMediaMutation) {
  return {
    resource_stay_fulfill_sku_id: optionalText(payload.staySkuId),
    media_scene: payload.mediaScene,
    media_name: payload.mediaName.trim(),
    file_url: payload.fileUrl.trim(),
    file_ext: optionalText(payload.fileExt),
    content_text: optionalText(payload.contentText),
    sort_no: payload.sortNo,
    remark: payload.remark.trim(),
  };
}

export function mapStayResourceMediaOrder(items: StayResourceMediaOrderItem[]) {
  return {
    items: items.map((item) => ({
      media_id: item.stayMediaId,
      sort_no: item.sortNo,
    })),
  };
}

export function mapStayResourceGeoReview(payload: StayResourceGeoReviewMutation) {
  return {
    action: payload.decision,
    candidate_id: optionalText(payload.candidateId),
    review_note: payload.reason.trim(),
  };
}

export function mapStayResourceSupplierMutation(payload: StayResourceSupplierMutation) {
  return {
    stay_sku_id: optionalText(payload.staySkuId),
    supplier_id: payload.supplierId.trim(),
    procurement_channel_type: payload.procurementChannelType,
    cooperation_status: payload.cooperationStatus,
    valid_from: optionalText(payload.validFrom),
    valid_to: optionalText(payload.validTo),
    evidence_type: optionalText(payload.evidenceType),
    evidence_id: optionalText(payload.evidenceId),
    evidence_summary: optionalText(payload.evidenceSummary),
    review_status: payload.reviewStatus,
    remark: payload.remark.trim(),
  };
}

export function mapStayResourceSpu(raw: StayResourceSpuRaw): StayResourceSpuItem {
  return {
    publicPhones: raw.public_phones ?? undefined,
    roomCount: raw.room_count ?? undefined,
    openingYear: raw.opening_year ?? undefined,
    renovationYear: raw.renovation_year ?? undefined,
    platformRating: raw.platform_rating ?? undefined,
    staySpuId: raw.resource_stay_fulfill_spu_id,
    hotelName: raw.hotel_name,
    hotelShortName: raw.hotel_short_name ?? undefined,
    country: raw.country,
    province: raw.province,
    city: raw.city,
    district: raw.district ?? undefined,
    address: raw.address ?? undefined,
    lng: decimal(raw.lng),
    lat: decimal(raw.lat),
    stayLevel: raw.stay_level ?? undefined,
    hotelType: raw.hotel_type ?? undefined,
    reviewStatus: raw.review_status,
    reviewedBy: raw.reviewed_by == null ? undefined : String(raw.reviewed_by),
    reviewedAt: raw.reviewed_at ?? undefined,
    reviewReason: raw.review_reason ?? undefined,
    createdAt: raw.created_at,
    updatedAt: raw.updated_at,
    createdBy: raw.created_by,
    updatedBy: raw.updated_by,
    isDisabled: raw.is_disabled,
    status: raw.status,
    remark: raw.remark,
    skuCount: raw.sku_count,
    mediaCount: raw.media_count,
    supplierCount: raw.supplier_count,
    geoStatus: raw.geo_status,
    geoVerificationStatus: raw.geo_verification_status,
    geoAmapPoiId: raw.geo_amap_poi_id ?? undefined,
    geoCoordinateSystem: raw.geo_coordinate_system ?? undefined,
    geoSource: raw.geo_source ?? undefined,
    geoSelectedCandidateId: raw.geo_selected_candidate_id ?? undefined,
    geoLastVerifiedAt: raw.geo_last_verified_at ?? undefined,
    geoReviewedBy: raw.geo_reviewed_by == null ? undefined : String(raw.geo_reviewed_by),
    geoReviewedAt: raw.geo_reviewed_at ?? undefined,
    geoFailureReason: raw.geo_failure_reason ?? undefined,
  };
}

export function mapStayResourceListPage(raw: StayResourceListPageRaw): StayResourceListPage {
  return {
    items: (raw.items ?? []).map(mapStayResourceSpu),
    total: raw.total,
    page: raw.page,
    pageSize: raw.page_size,
  };
}

export function mapStayResourceSku(raw: StayResourceSkuRaw): StayResourceSkuItem {
  return {
    staySkuId: raw.resource_stay_fulfill_sku_id,
    staySpuId: raw.resource_stay_fulfill_spu_id,
    skuName: raw.sku_name,
    roomType: raw.room_type,
    roomUsageLabel: raw.room_usage_label ?? undefined,
    roomCapacity: raw.room_capacity ?? undefined,
    createdAt: raw.created_at,
    updatedAt: raw.updated_at,
    createdBy: raw.created_by,
    updatedBy: raw.updated_by,
    isDisabled: raw.is_disabled,
    status: raw.status,
    remark: raw.remark,
  };
}

export function mapStayResourceMedia(raw: StayResourceMediaRaw): StayResourceMediaItem {
  return {
    stayMediaId: raw.resource_stay_media_id,
    staySpuId: raw.resource_stay_fulfill_spu_id,
    staySkuId: raw.resource_stay_fulfill_sku_id ?? undefined,
    mediaScene: raw.media_scene,
    mediaName: raw.media_name,
    fileUrl: raw.file_url,
    fileExt: raw.file_ext ?? undefined,
    contentText: raw.content_text ?? undefined,
    sortNo: raw.sort_no,
    mediaStatus: raw.media_status,
    createdAt: raw.created_at,
    updatedAt: raw.updated_at,
    remark: raw.remark,
  };
}

export function mapStayResourceGeoCandidate(
  raw: StayResourceGeoCandidateRaw,
): StayResourceGeoCandidate {
  return {
    candidateId: raw.resource_stay_geo_candidate_id,
    verificationRunId: raw.verification_run_id,
    sequenceNo: raw.candidate_seq,
    provider: raw.provider,
    poiId: raw.poi_id,
    name: raw.candidate_name,
    typeName: raw.type_name,
    address: raw.address,
    province: raw.province,
    city: raw.city,
    district: raw.district ?? undefined,
    lng: decimalRequired(raw.lng),
    lat: decimalRequired(raw.lat),
    coordinateSystem: raw.coordinate_system,
    searchTerm: raw.search_term,
    verificationStage: raw.verification_stage,
    score: raw.match_score === null ? undefined : decimalRequired(raw.match_score),
    qualified: raw.is_qualified,
    qualificationReason: raw.qualification_reason,
    createdAt: raw.created_at,
  };
}

export function mapStayResourceGeoVerificationResult(
  raw: StayResourceGeoVerificationResultRaw,
): StayResourceGeoVerificationResult {
  return {
    verificationRunId: raw.verification_run_id,
    status: raw.status,
    candidates: (raw.candidates ?? []).map(mapStayResourceGeoCandidate),
    selectedCandidateId: raw.selected_candidate_id ?? undefined,
    failureReason: raw.failure_reason ?? undefined,
  };
}

export function mapStayResourceGeoReviewResult(
  raw: StayResourceGeoReviewResultRaw,
): StayResourceGeoReviewResult {
  return {
    staySpuId: raw.stay_spu_id,
    verificationRunId: raw.verification_run_id,
    status: raw.status,
    selectedCandidateId: raw.selected_candidate_id ?? undefined,
  };
}

export function mapStayResourceGeoReviewAudit(
  raw: StayResourceGeoReviewAuditRaw,
): StayResourceGeoReviewAuditItem {
  return {
    auditId: raw.resource_stay_geo_review_audit_id,
    staySpuId: raw.stay_spu_id,
    verificationRunId: raw.verification_run_id,
    candidateId: raw.candidate_id ?? undefined,
    reviewAction: raw.review_action,
    reviewNote: raw.review_note,
    beforeGeo: raw.before_geo_json,
    afterGeo: raw.after_geo_json,
    createdAt: raw.created_at,
    createdBy: raw.created_by,
    createdByName: raw.created_by_name ?? undefined,
  };
}

export function mapStayResourceSupplier(raw: StayResourceSupplierRaw): StayResourceSupplierItem {
  return {
    capabilityId: raw.resource_stay_supplier_capability_id,
    staySpuId: raw.stay_spu_id,
    staySkuId: raw.stay_sku_id ?? undefined,
    skuName: raw.sku_name ?? undefined,
    supplierId: raw.supplier_id,
    supplierName: raw.supplier_name,
    procurementChannelType: raw.procurement_channel_type,
    cooperationStatus: raw.cooperation_status,
    validFrom: raw.valid_from ?? undefined,
    validTo: raw.valid_to ?? undefined,
    evidenceType: raw.evidence_type ?? undefined,
    evidenceId: raw.evidence_id ?? undefined,
    evidenceSummary: raw.evidence_summary ?? undefined,
    reviewStatus: raw.review_status,
    remark: raw.remark,
  };
}

export function mapStayResourceSupplierOption(
  raw: StayResourceSupplierOptionRaw,
): StayResourceSupplierOption {
  return {
    supplierId: raw.supplier_id,
    supplierCode: raw.supplier_code,
    supplierName: raw.supplier_name,
    supplierShortName: raw.supplier_short_name ?? undefined,
    supplierType: raw.supplier_type,
    companySubjectName: raw.company_subject_name ?? undefined,
    canBeFulfillmentSubject: raw.can_be_fulfillment_subject,
    contactName: raw.contact_name ?? undefined,
    contactPhone: raw.contact_phone ?? undefined,
  };
}

export function mapStayResourceHousLevel(
  raw: StayResourceHousLevelRaw,
): StayResourceHousLevelOption {
  return {
    housLevelId: raw.hous_level_id,
    levelCode: raw.level_code,
    levelName: raw.level_name,
  };
}

export function mapStayResourceLegacyMapping(
  raw: StayResourceLegacyMappingRaw,
): StayResourceLegacyMappingItem {
  return {
    mappingId: raw.resource_legacy_mapping_id,
    sourceSystem: raw.legacy_source_system,
    sourceTable: raw.legacy_source_table,
    legacyId: raw.legacy_id,
    reviewStatus: raw.review_status,
    updatedAt: raw.updated_at,
  };
}

export function isStayResourceSpuRaw(value: unknown): value is StayResourceSpuRaw {
  return isRecord(value)
    && (value.public_phones == null
      || (Array.isArray(value.public_phones) && value.public_phones.every((v) => typeof v === "string")))
    && ["room_count", "opening_year", "renovation_year", "platform_rating"].every(
      (key) => value[key] == null || (typeof value[key] === "number" && Number.isFinite(value[key])),
    )
    && strings(value, [
      "resource_stay_fulfill_spu_id", "hotel_name", "country", "province", "city",
      "created_at", "updated_at", "status", "remark", "geo_status",
    ])
    && nullableStrings(value, [
      "hotel_short_name", "district", "address", "lng", "lat", "stay_level", "hotel_type",
    ])
    && optionalNullableStrings(value, [
      "geo_amap_poi_id", "geo_coordinate_system", "geo_source", "geo_selected_candidate_id",
      "geo_last_verified_at", "geo_reviewed_at", "geo_failure_reason",
      "geo_current_verification_run_id",
    ])
    && (value.geo_verification_status === undefined
      || isGeoVerificationStatus(value.geo_verification_status))
    && isReviewStatus(value.review_status)
    && optionalStringOrNumber(value.reviewed_by)
    && optionalNullableStrings(value, ["reviewed_at", "review_reason"])
    && optionalStringOrNumber(value.geo_reviewed_by)
    && numbers(value, ["created_by", "updated_by", "sku_count", "media_count", "supplier_count"])
    && typeof value.is_disabled === "boolean";
}

export function isStayResourceListPageRaw(value: unknown): value is StayResourceListPageRaw {
  return isRecord(value)
    && (value.items === undefined
      || (Array.isArray(value.items) && value.items.every(isStayResourceSpuRaw)))
    && numbers(value, ["total", "page", "page_size"]);
}

export function isStayResourceSkuRaw(value: unknown): value is StayResourceSkuRaw {
  return isRecord(value)
    && strings(value, [
      "resource_stay_fulfill_sku_id", "resource_stay_fulfill_spu_id", "sku_name", "room_type",
      "created_at", "updated_at", "status", "remark",
    ])
    && nullableStrings(value, ["room_usage_label"])
    && (value.room_capacity === null || typeof value.room_capacity === "number")
    && numbers(value, ["created_by", "updated_by"])
    && typeof value.is_disabled === "boolean";
}

export function isStayResourceMediaRaw(value: unknown): value is StayResourceMediaRaw {
  return isRecord(value)
    && strings(value, [
      "resource_stay_media_id", "resource_stay_fulfill_spu_id", "media_scene", "media_name",
      "file_url", "media_status", "created_at", "updated_at", "remark",
    ])
    && nullableStrings(value, ["file_ext", "content_text"])
    && optionalNullableStrings(value, ["resource_stay_fulfill_sku_id"])
    && numbers(value, ["sort_no"]);
}

export function isStayResourceGeoCandidateRaw(
  value: unknown,
): value is StayResourceGeoCandidateRaw {
  return isRecord(value)
    && strings(value, [
      "resource_stay_geo_candidate_id", "verification_run_id", "stay_spu_id", "provider",
      "poi_id", "candidate_name", "type_name", "province", "city", "district", "address",
      "coordinate_system", "search_term", "verification_stage", "qualification_reason",
      "created_at",
    ])
    && nullableStrings(value, ["raw_response_sha256"])
    && numbers(value, ["candidate_seq", "created_by"])
    && decimalValue(value.lng)
    && decimalValue(value.lat)
    && (value.match_score === null || decimalValue(value.match_score))
    && typeof value.is_qualified === "boolean";
}

export function isStayResourceGeoVerificationResultRaw(
  value: unknown,
): value is StayResourceGeoVerificationResultRaw {
  return isRecord(value)
    && typeof value.verification_run_id === "string"
    && isGeoVerificationStatus(value.status)
    && Array.isArray(value.candidates)
    && value.candidates.every(isStayResourceGeoCandidateRaw)
    && (value.selected_candidate_id === null || typeof value.selected_candidate_id === "string")
    && (value.failure_reason === null || typeof value.failure_reason === "string");
}

export function isStayResourceGeoReviewResultRaw(
  value: unknown,
): value is StayResourceGeoReviewResultRaw {
  return isRecord(value)
    && strings(value, ["stay_spu_id", "verification_run_id"])
    && isGeoVerificationStatus(value.status)
    && (value.selected_candidate_id === null || typeof value.selected_candidate_id === "string");
}

export function isStayResourceGeoReviewAuditRaw(
  value: unknown,
): value is StayResourceGeoReviewAuditRaw {
  return isRecord(value)
    && strings(value, [
      "resource_stay_geo_review_audit_id", "stay_spu_id", "verification_run_id",
      "review_action", "review_note", "created_at",
    ])
    && (value.candidate_id === null || typeof value.candidate_id === "string")
    && ["auto_approve", "approve", "reject"].includes(String(value.review_action))
    && isRecord(value.before_geo_json)
    && isRecord(value.after_geo_json)
    && typeof value.created_by === "number";
}

export function isStayResourceSupplierRaw(value: unknown): value is StayResourceSupplierRaw {
  return isRecord(value)
    && strings(value, [
      "resource_stay_supplier_capability_id", "stay_spu_id", "supplier_id", "supplier_name",
      "procurement_channel_type", "cooperation_status", "review_status", "remark",
    ])
    && nullableStrings(value, [
      "stay_sku_id", "sku_name", "valid_from", "valid_to", "evidence_type", "evidence_id",
      "evidence_summary",
    ]);
}

export function isStayResourceSupplierOptionRaw(
  value: unknown,
): value is StayResourceSupplierOptionRaw {
  return isRecord(value)
    && strings(value, ["supplier_id", "supplier_code", "supplier_name", "supplier_type"])
    && nullableStrings(value, [
      "supplier_short_name", "company_subject_name", "contact_name", "contact_phone",
    ])
    && typeof value.can_be_fulfillment_subject === "boolean";
}

export function isStayResourceHousLevelRaw(
  value: unknown,
): value is StayResourceHousLevelRaw {
  return isRecord(value)
    && strings(value, ["hous_level_id", "level_code", "level_name"]);
}

export function isStayResourceLegacyMappingRaw(
  value: unknown,
): value is StayResourceLegacyMappingRaw {
  return isRecord(value)
    && strings(value, [
      "resource_legacy_mapping_id", "legacy_source_system", "legacy_source_table", "legacy_id",
      "review_status", "updated_at",
    ]);
}

function optionalText(value?: string): string | undefined {
  const normalized = value?.trim();
  return normalized || undefined;
}

function decimal(value: string | number | null): number | undefined {
  if (value === null) return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function decimalRequired(value: string | number): number {
  return Number(value);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function strings(value: Record<string, unknown>, keys: string[]): boolean {
  return keys.every((key) => typeof value[key] === "string");
}

function nullableStrings(value: Record<string, unknown>, keys: string[]): boolean {
  return keys.every((key) => value[key] === null || typeof value[key] === "string");
}

function optionalNullableStrings(value: Record<string, unknown>, keys: string[]): boolean {
  return keys.every((key) => value[key] === undefined
    || value[key] === null
    || typeof value[key] === "string");
}

function numbers(value: Record<string, unknown>, keys: string[]): boolean {
  return keys.every((key) => isFiniteNumber(value[key]));
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

function decimalValue(value: unknown): boolean {
  return (typeof value === "string" || typeof value === "number")
    && value !== ""
    && Number.isFinite(Number(value));
}

function optionalStringOrNumber(value: unknown): boolean {
  return value === undefined || value === null || typeof value === "string" || isFiniteNumber(value);
}

function isGeoVerificationStatus(value: unknown): value is StayGeoVerificationStatus {
  return typeof value === "string" && [
    "unverified", "pending", "resolved", "review_required", "rejected", "failed",
  ].includes(value);
}

function isReviewStatus(value: unknown): value is StayResourceReviewStatus {
  return ["pending", "approved", "rejected"].includes(String(value));
}
